import type { ServerResponseV1 } from "@/http"
import * as Sentry from "@sentry/vue"
import { z } from "zod"
import { v1 } from "@/axios"

interface DocumentPresignFile {
  name?: string
  type?: string
  size?: number | null
}

const documentPresignedUrlSchemaV1 = z.object({
  eventDocumentID: z.number(),
  uploadUrl: z.string(),
  key: z.string(),
  fileName: z.string(),
}).transform(data => ({
  eventDocumentId: data.eventDocumentID,
  uploadUrl: data.uploadUrl,
  key: data.key,
  fileName: data.fileName,
}))

export type DocumentPresignedUrlResponse = z.infer<typeof documentPresignedUrlSchemaV1>

function parseDocumentPresignedUrlResponse(raw: unknown, context: { endpoint: string, method: string }): DocumentPresignedUrlResponse {
  const result = documentPresignedUrlSchemaV1.safeParse(raw)

  if (!result.success) {
    const message = "Failed to validate API response schema"
    Sentry.logger.error(message, { raw, ...context, error: result.error })
    throw new Error(message, { cause: result.error })
  }

  return result.data
}

export async function getDocumentPresignedUrl(file: DocumentPresignFile, eventVaultId: number) {
  const endpoint = `/events/${eventVaultId}/documents/presigned`
  const response = await v1.post<ServerResponseV1<unknown>>(endpoint, {
    fileName: file.name ?? "document",
    contentType: file.type || "application/octet-stream",
    fileSize: file.size,
  })

  return { data: parseDocumentPresignedUrlResponse(response.data.data, { endpoint, method: "post" }) }
}
